<!--
  飞书告警机器人面板（独立板块：运维排障 → 飞书告警）。

  用途：把线上未预期异常（HTTP 500）推到飞书群，出问题不用等用户反馈。
  这里只做三件事：看链路状态、发一条测试消息、翻最近的发送记录。

  ⚠️ webhook 与签名密钥存在「系统设置 → 告警」里，本页只显示**掩码**
     （`https://open.feishu.cn/...abcd`）—— access token 就在路径最后一段，
     页面能读到全文等于任何拿到管理员会话的人都能往群里灌消息。
     接口也只返回掩码，前端没有全文可拿，不要试图在这里做「查看/编辑」。

  ⚠️ 「测试发送」是同步接口，且**跳过告警开关与节流**：
     运维点测试就是要立刻知道通不通，若还被开关或去重窗口拦掉，
     页面显示成功而群里什么都没有，反而制造新的困惑。每次调用都会写 ALERT_TEST 操作日志。
-->
<template>
  <div>
    <div v-if="statusLoaded && !status.webhookConfigured" class="adm-alert is-warning adm-alert-gap">
      <i class="el-icon-warning-outline" />
      <span>
        未检测到 webhook：请到「系统设置 → 告警 → 飞书机器人 Webhook」填写并保存，
        或配置环境变量 <code>{{ status.webhookEnvName }}</code>。配置后本页点「发送测试消息」即可验证。
      </span>
    </div>

    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />告警链路
          <span class="adm-card-title-extra">
            只有系统故障（未预期的错误）会推送；业务提示与频率限制不推送
          </span>
        </div>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-s-promotion"
          :loading="testing"
          :disabled="!statusLoaded"
          @click="handleTest"
        >
          发送测试消息
        </el-button>
      </div>

      <div class="adm-card-body">
        <div class="adm-stat-grid">
          <div class="adm-stat">
            <div class="adm-stat-label">
              <span class="adm-stat-icon" :class="status.enabled ? 'is-success' : 'is-neutral'">
                <i class="el-icon-bell" />
              </span>告警开关
            </div>
            <div class="adm-stat-value" :class="{ 'is-warn': !status.enabled }">
              {{ status.enabled ? '已开启' : '已关闭' }}
            </div>
            <div class="adm-stat-hint">系统设置 → 告警</div>
          </div>

          <div class="adm-stat">
            <div class="adm-stat-label">
              <span class="adm-stat-icon" :class="status.webhookConfigured ? 'is-success' : 'is-danger'">
                <i class="el-icon-link" />
              </span>Webhook
            </div>
            <div class="adm-stat-value" :class="{ 'is-danger': !status.webhookConfigured }">
              {{ status.webhookConfigured ? '已配置' : '未配置' }}
            </div>
            <div class="adm-stat-hint adm-alert-ellipsis" :title="status.webhookMasked">
              {{ status.webhookConfigured
                ? status.webhookMasked + '（' + sourceText(status.webhookSource) + '）'
                : '去「系统设置 → 告警」填写' }}
            </div>
          </div>

          <div class="adm-stat">
            <div class="adm-stat-label">
              <span class="adm-stat-icon" :class="status.secretConfigured ? 'is-success' : 'is-neutral'">
                <i class="el-icon-lock" />
              </span>签名校验
            </div>
            <div class="adm-stat-value">{{ status.secretConfigured ? '已启用' : '未启用' }}</div>
            <div class="adm-stat-hint">
              {{ status.secretConfigured ? '来源：' + sourceText(status.secretSource) : '机器人未开签名校验' }}
            </div>
          </div>

          <div class="adm-stat">
            <div class="adm-stat-label">
              <span class="adm-stat-icon" :class="status.ready ? 'is-success' : 'is-warn'">
                <i class="el-icon-circle-check" />
              </span>链路状态
            </div>
            <div class="adm-stat-value" :class="{ 'is-warn': !status.ready }">
              {{ status.ready ? '可用' : '不可用' }}
            </div>
            <div class="adm-stat-hint">{{ readyHint }}</div>
          </div>
        </div>

        <div class="adm-desc adm-alert-desc">
          <div class="adm-desc-item">
            <span class="adm-desc-label">每分钟上限</span>
            <span class="adm-desc-value">{{ status.maxPerMinute }} 条</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">同类去重</span>
            <span class="adm-desc-value">{{ status.dedupeMinutes }} 分钟内只发一次</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">服务器时间</span>
            <span class="adm-desc-value">
              {{ status.serverTime || '—' }}
              <span class="adm-muted">（{{ status.serverEpochSecond }}，签名用的就是这个时间戳）</span>
            </span>
          </div>
          <div class="adm-desc-item is-full">
            <span class="adm-desc-label">触发范围</span>
            <span class="adm-desc-value">
              未捕获异常（GlobalExceptionHandler 的兜底分支）。
              去重种子是「异常类型 + 出错位置」而不是异常消息 ——
              同一个 bug 在不同请求下消息可能千差万别，按消息去重等于没去重。
            </span>
          </div>
          <div class="adm-desc-item is-full">
            <span class="adm-desc-label">凭据位置</span>
            <span class="adm-desc-value">
              webhook 与签名密钥存在「系统设置 → 告警」里（敏感项）：本页只显示<b>掩码</b>，
              操作日志只记「已更新」不记取值。保存时<b>留空表示不修改</b>；
              要清空请用「恢复默认」，清空后回退到环境变量
              <code>{{ status.webhookEnvName }}</code> / <code>{{ status.secretEnvName }}</code>。
            </span>
          </div>
          <div class="adm-desc-item is-full">
            <span class="adm-desc-label">19021 怎么查</span>
            <span class="adm-desc-value">
              飞书把三种原因合成了一条文案，用上面的「服务器时间」先分一半：
              <b>时间差超过 1 小时 → 服务器时钟偏了</b>，先校时。
              时间对得上则按顺序查：① 密钥是否与机器人「安全设置 → 签名校验」当前显示的一致
              （飞书每次重新开启签名校验都会生成新密钥）；
              ② 服务端是否已升级到最新版本（旧版本的签名计算方式有误，升级即可修复）；
              ③ 机器人与 webhook 是否属于同一个群。
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="signFailures" class="adm-alert is-warning adm-alert-gap">
      <i class="el-icon-warning-outline" />
      <span>
        最近 {{ signFailures }} 条发送被飞书以 <b>19021 签名校验失败</b>拒绝。
        先对照上方「服务器时间」排除时钟问题，再依次核对：签名密钥是否为机器人当前值、
        服务端是否已升级到最新版本（旧版本的签名计算方式有误）、
        机器人与 webhook 是否属于同一个群。
      </span>
    </div>

    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />最近发送记录
          <span class="adm-card-title-extra">
            最多保留 50 条、保存 7 天；只记录真正发起过发送的条目
          </span>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="logsLoading" @click="loadLogs">
          刷新
        </el-button>
      </div>

      <div v-if="logsLoading && !logs.length" class="adm-empty">
        <i class="el-icon-loading" />加载中…
      </div>
      <div v-else-if="!logs.length" class="adm-empty">
        <i class="el-icon-message" />还没有发送记录
      </div>
      <el-table v-else v-loading="logsLoading" :data="logs" border>
        <el-table-column prop="time" label="时间" width="170" />
        <el-table-column label="触发方式" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.trigger === 'TEST' ? 'warning' : 'info'">
              {{ row.triggerLabel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <span :class="row.success ? 'adm-alert-ok' : 'adm-alert-fail'">
              <i :class="row.success ? 'el-icon-success' : 'el-icon-error'" />
              {{ row.success ? '成功' : '失败' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
        <el-table-column prop="detail" label="说明" min-width="240" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="adm-muted">{{ row.detail }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { fetchAlertStatus, fetchAlertLogs, sendAlertTest, isOk } from '@/api/admin'

/** 空状态默认值：接口未返回前不要出现 undefined */
const EMPTY_STATUS = {
  webhookConfigured: false,
  webhookMasked: '',
  webhookSource: 'NONE',
  secretConfigured: false,
  secretSource: 'NONE',
  enabled: false,
  ready: false,
  maxPerMinute: 0,
  dedupeMinutes: 0,
  webhookEnvName: 'FEISHU_WEBHOOK_URL',
  secretEnvName: 'FEISHU_WEBHOOK_SECRET',
  webhookSettingKey: 'alert.feishu.webhook_url',
  secretSettingKey: 'alert.feishu.secret',
  serverTime: '',
  serverEpochSecond: 0
}

export default {
  name: 'AlertPanel',
  data() {
    return {
      status: { ...EMPTY_STATUS },
      statusLoaded: false,
      testing: false,
      logs: [],
      logsLoading: false
    }
  },
  computed: {
    /** 「不可用」时告诉运维到底缺哪一半，避免只看到红字还得自己去猜 */
    readyHint() {
      if (this.status.ready) return '异常会推送到飞书群'
      if (!this.status.webhookConfigured) return '缺少 webhook'
      return '告警开关未开启'
    },
    /**
     * 最近记录里有多少条是 19021（签名失败）。
     * 单独拎出来做成横幅：这条错误码最容易误判成「代码坏了」，
     * 实际九成是密钥被重新生成过，页面必须直接指出下一步做什么。
     */
    signFailures() {
      return this.logs.filter(item => !item.success && String(item.detail || '').indexOf('19021') >= 0).length
    }
  },
  created() {
    this.loadStatus()
    this.loadLogs()
  },
  methods: {
    /** 取值来源转中文：改完数据库却没生效时，看一眼来源就知道是不是被环境变量盖住了 */
    sourceText(source) {
      if (source === 'DATABASE') return '数据库'
      if (source === 'ENV') return '环境变量'
      return '未配置'
    },
    loadStatus() {
      return fetchAlertStatus()
        .then(res => {
          if (!isOk(res)) throw new Error(res && res.message ? res.message : '加载失败')
          const data = res.data || {}
          // ⚠️ 后端把 Long 序列化成字符串，数字字段统一转一下再参与判断
          this.status = {
            ...EMPTY_STATUS,
            ...data,
            maxPerMinute: Number(data.maxPerMinute || 0),
            dedupeMinutes: Number(data.dedupeMinutes || 0),
            serverEpochSecond: Number(data.serverEpochSecond || 0)
          }
          this.statusLoaded = true
        })
        .catch(err => {
          this.$message.error(err.message || '加载告警状态失败')
        })
    },
    loadLogs() {
      this.logsLoading = true
      return fetchAlertLogs(20)
        .then(res => {
          if (!isOk(res)) throw new Error(res && res.message ? res.message : '加载失败')
          this.logs = res.data || []
        })
        .catch(err => {
          this.logs = []
          this.$message.error(err.message || '加载告警记录失败')
        })
        .finally(() => {
          this.logsLoading = false
        })
    },
    /**
     * 测试发送前先确认：这条消息会真的发到生产群，群里所有人都能看到。
     * 不做二次确认的话，误点一次就在群里留下一条莫名其妙的记录。
     */
    handleTest() {
      this.$confirm(
        '将立即向飞书群发送一条测试消息（跳过告警开关与节流限制），群里所有成员都能看到。',
        '发送测试消息',
        { confirmButtonText: '发送', cancelButtonText: '取消', type: 'warning' }
      )
        .then(() => this.doSendTest())
        .catch(() => {})
    },
    doSendTest() {
      this.testing = true
      return sendAlertTest()
        .then(res => {
          if (!isOk(res)) throw new Error(res && res.message ? res.message : '发送失败')
          this.$message.success('测试消息已发送，请到飞书群确认')
        })
        .catch(err => {
          // 失败原因由后端拼好（如「未配置 webhook」「飞书返回 code=19021：sign match fail」），
          // 用 8 秒长提示，别让运维还没看清就消失
          this.$message({ type: 'error', message: err.message || '发送失败', duration: 8000 })
        })
        .finally(() => {
          this.testing = false
          this.loadLogs()
        })
    }
  }
}
</script>

<style scoped>
.adm-alert-gap {
  margin-bottom: 16px;
}

/* 与统计卡之间留出呼吸空间；上边距而不是下边距，避免最后一个 card 多出空白 */
.adm-alert-desc {
  margin-top: 16px;
}

.adm-alert-ok {
  color: #52c41a;
}

.adm-alert-fail {
  color: var(--adm-danger);
}

.adm-alert-ok i,
.adm-alert-fail i {
  margin-right: 4px;
}

/* 掩码地址比统计卡默认宽度长，单行省略 + title 悬浮看全文 */
.adm-alert-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
